// Rugby-specific match scoring -- iteration 081, renamed in 098.
// Netball has its own parallel file (types/netball-score.ts) -- no shared
// abstraction across sports. See feedback_sport_scoring_no_abstraction.md.

export const RUGBY_SCORE_EVENT_TYPES = [
  "try",
  "conversion",
  "penalty",
  "drop_goal",
  "yellow_card",
  "red_card",
  // Set-piece stats (084c prep). Recorded via API only for now -- no UI
  // buttons yet. Each entry is a single event so totals can be derived by
  // counting per type, per team_side.
  "scrum_won",
  "scrum_lost",
  "lineout_won",
  "lineout_lost",
  "correction",
] as const;
export type RugbyScoreEventType = (typeof RUGBY_SCORE_EVENT_TYPES)[number];

export const TEAM_SIDES = ["home", "away"] as const;
export type TeamSide = (typeof TEAM_SIDES)[number];

export interface RugbyScoreBreakdown {
  tries: number;
  conversions: number;
  penalties: number;
  drop_goals: number;
}

export interface RugbyMatchResultTeamSummary {
  team_id: string | null;
  team_display_name: string | null;
  school_id: string | null;
  school_display_name: string | null;
  school_abbreviation: string | null;
  /** 085: joined fresh from schools row; null when school unbranded. */
  school_logo_url: string | null;
  school_primary_color: string | null;
  school_secondary_color: string | null;
}

export interface RugbyMatchResultPublic {
  match_id: string;
  event_id: string;
  /** Match scheduled kick-off (ISO) -- used for the half-aware clock. */
  starts_at: string;
  /** Scheduled lengths in minutes -- used for the clock's phase progression. */
  first_half_mins: number;
  break_mins: number;
  second_half_mins: number;
  /** Age group label (e.g. "U14") -- for scoreboard subtitle. */
  age_group: string | null;
  /** Referee name -- nullable; shown next to the clock when present. */
  referee: string | null;
  home_team: RugbyMatchResultTeamSummary | null;
  away_team: RugbyMatchResultTeamSummary | null;
  home_score: number;
  away_score: number;
  home_breakdown: RugbyScoreBreakdown;
  away_breakdown: RugbyScoreBreakdown;
  final: boolean;
  last_event_at: string | null;
  created_at: string;
  updated_at: string;
  /**
   * True if any of the requesting user's linked children is on one of
   * the participating teams. Drives the default state of the per-match
   * vibrate toggle on the client.
   */
  my_child_playing: boolean;
  _actions: {
    can_record: boolean;
    can_finalize: boolean;
    can_correct: boolean;
  };
}

export interface RugbyScoreEventPublic {
  id: string;
  match_id: string;
  team_side: TeamSide;
  event_type: RugbyScoreEventType;
  points: number;
  minute: number | null;
  player_id: string | null;
  player_display_name: string | null;
  recorded_by: string;
  recorder_display_name: string | null;
  note: string | null;
  created_at: string;
}

export interface RugbyScoreResponse {
  result: RugbyMatchResultPublic;
  events: RugbyScoreEventPublic[];
}

// --- League table ---

export interface RugbyLeagueTableRow {
  team_id: string;
  team_display_name: string;
  school_id: string;
  school_display_name: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  points_for: number;
  points_against: number;
  points_diff: number;
  try_bonuses: number;
  losing_bonuses: number;
  league_points: number;
}

export interface RugbyLeagueTableResponse {
  rows: RugbyLeagueTableRow[];
}

// --- Event results ---

export interface RugbyEventMatchResultSummary {
  match_id: string;
  match_name: string;
  starts_at: string;
  home_team: RugbyMatchResultTeamSummary | null;
  away_team: RugbyMatchResultTeamSummary | null;
  home_score: number;
  away_score: number;
  final: boolean;
  winner_side: TeamSide | "draw" | null;
  /** See RugbyMatchResultPublic.my_child_playing. */
  my_child_playing: boolean;
}

export interface RugbyEventResultsResponse {
  event_id: string;
  games_played: number;
  games_final: number;
  home_school_summary: {
    school_id: string | null;
    won: number;
    lost: number;
    drawn: number;
    points_for: number;
    points_against: number;
  };
  matches: RugbyEventMatchResultSummary[];
}
